// controls.jsx — the small set of design-system-conformant, presentational
// sub-controls the four WL-011 block editors compose. WL-011 (Fable Hail Mary
// Wave 2).
//
// WHY PRESENTATIONAL: the live editors (Container/Settings, Image/ImageSettings,
// Text/Form) are craftjs + Redux coupled and depend on heavy app-internal shared
// components (ColorPopup, MarginBox, SettingsSlider…) that cannot render outside
// the running app. These controls mirror those surfaces 1:1 (every field the
// existing editor + the deck exposes) as controlled props-in / onChange-out
// widgets, so the four editors are feature-complete AND render standalone in the
// isolated harness (the CRA build is the known-broken local infra gap). The live
// craftjs Settings wrappers adopt these incrementally — see WL-011 remaining[].
//
// Tokens: DM Sans, ink #123051, brand #9B5AF8, focus ring #9B5AF8. All controls
// are keyboard-operable, >=44px targets, visible focus, reduced-motion safe.

import React, { useId } from "react";
import styled from "styled-components";
import { fontFamily } from "../../../theme/tokens";
import EditorField, { fieldChrome, fieldFocusRing, FieldLabel } from "./EditorField";

// Field palette + focus ring now come from ONE source (EditorField / tokens.field)
// — these are aliases so the rest of this module reads unchanged instead of
// re-declaring the hexes it used to (that duplication was the whole finding).
const INK = fieldChrome.ink;
const MUTED = fieldChrome.muted;
const PURPLE = fieldChrome.accent;
const BORDER = fieldChrome.border;
const FIELD_BG = fieldChrome.bg;
const focusRing = fieldFocusRing;

// FieldLabel is imported from EditorField (single source). Row stays local (it is
// a layout wrapper, not field chrome).
const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  min-height: 44px;
  font-family: ${fontFamily};
`;

// ── TextInputField (EW-014/015: shows validation error) ───────────────────────
// Now a thin adaptor over the shared <EditorField> so the text field, its label,
// error, helper and counter come from the ONE component every editor shares.
export const TextInputField = (props) => <EditorField as="input" {...props} />;

// ── LabeledSlider (Corner Rounding, Line Spacing, Resizing, Height…) ──────────
const SliderWrap = styled.div`
  font-family: ${fontFamily};
`;
const SliderTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 6px;
`;
const SliderName = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: ${INK};
`;
const SliderVal = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: ${MUTED};
  min-width: 44px;
  text-align: right;
`;
const Range = styled.input`
  width: 100%;
  height: 4px;
  -webkit-appearance: none;
  appearance: none;
  background: linear-gradient(90deg, ${PURPLE} var(--pct, 0%), ${BORDER} var(--pct, 0%));
  border-radius: 4px;
  outline: none;
  margin: 12px 0;
  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    width: 20px; height: 20px;
    border-radius: 50%;
    background: #fff;
    border: 2px solid ${PURPLE};
    cursor: pointer;
    box-shadow: 0 1px 3px rgba(18,48,81,0.2);
  }
  &::-moz-range-thumb {
    width: 20px; height: 20px; border-radius: 50%;
    background: #fff; border: 2px solid ${PURPLE}; cursor: pointer;
  }
  ${focusRing}
`;

export const LabeledSlider = ({ label, value, min = 0, max = 100, step = 1, unit = "", onChange }) => {
  const pct = ((Number(value) - min) / (max - min)) * 100;
  const id = useId();
  return (
    <SliderWrap>
      <SliderTop>
        <SliderName as="label" htmlFor={id}>{label}</SliderName>
        <SliderVal>{value}{unit}</SliderVal>
      </SliderTop>
      <Range
        id={id}
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        style={{ "--pct": `${pct}%` }}
        aria-valuetext={`${value}${unit}`}
        onChange={(e) => onChange && onChange(Number(e.target.value))}
      />
    </SliderWrap>
  );
};

// ── SegChoice (Size: Content Width / Full Width; Solid/Gradient…) ─────────────
const SegWrap = styled.div`
  display: inline-flex;
  padding: 3px;
  gap: 3px;
  background: #f2f4f7;
  border-radius: 10px;
  font-family: ${fontFamily};
`;
const SegBtn = styled.button`
  min-height: 44px;
  padding: 0 14px;
  border: none;
  border-radius: 8px;
  background: ${(p) => (p.$active ? "#fff" : "transparent")};
  color: ${(p) => (p.$active ? INK : MUTED)};
  font-family: ${fontFamily};
  font-size: 13px;
  font-weight: ${(p) => (p.$active ? 600 : 500)};
  cursor: pointer;
  box-shadow: ${(p) => (p.$active ? "0 1px 2px rgba(18,48,81,0.12)" : "none")};
  transition: background-color 0.2s ease, color 0.2s ease;
  ${focusRing}
  @media (prefers-reduced-motion: reduce) { transition: none; }
`;

export const SegChoice = ({ label, value, options, onChange }) => {
  const groupId = useId();
  return (
    <div>
      {label && <FieldLabel id={groupId}>{label}</FieldLabel>}
      <SegWrap role="radiogroup" aria-labelledby={label ? groupId : undefined}>
        {options.map((o) => (
          <SegBtn
            key={o.value}
            type="button"
            role="radio"
            aria-checked={value === o.value}
            $active={value === o.value}
            onClick={() => onChange && onChange(o.value)}
          >
            {o.label}
          </SegBtn>
        ))}
      </SegWrap>
    </div>
  );
};

// ── Toggle (Display Caption, Add Hyperlink, Show Label…) ──────────────────────
const ToggleTrack = styled.button`
  position: relative;
  width: 44px;
  height: 26px;
  flex: none;
  border: none;
  border-radius: 9999px;
  background: ${(p) => (p.$on ? PURPLE : "#cfd4dc")};
  cursor: pointer;
  transition: background-color 0.2s ease;
  ${focusRing}
  &::after {
    content: "";
    position: absolute;
    top: 3px;
    left: ${(p) => (p.$on ? "21px" : "3px")};
    width: 20px; height: 20px;
    border-radius: 50%;
    background: #fff;
    transition: left 0.2s ease;
  }
  @media (prefers-reduced-motion: reduce) { transition: none; &::after { transition: none; } }
`;
const ToggleLabel = styled.span`
  font-family: ${fontFamily};
  font-size: 13px;
  font-weight: 500;
  color: ${INK};
`;

export const Toggle = ({ label, checked, onChange }) => (
  <Row as="div">
    <ToggleLabel>{label}</ToggleLabel>
    <ToggleTrack
      type="button"
      role="switch"
      aria-checked={!!checked}
      aria-label={label}
      $on={!!checked}
      onClick={() => onChange && onChange(!checked)}
    />
  </Row>
);

// ── ColorField (Background Fill / Border / Drop Shadow via ColorPopup mirror) ──
const CFRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 44px;
`;
const Swatch = styled.button`
  width: 44px; height: 44px; flex: none;
  border-radius: 8px;
  border: 1px solid ${BORDER};
  cursor: pointer;
  /* checkerboard alpha backdrop (linear-gradient tiles; NOT a wheel) */
  background:
    ${(p) => p.$color || "transparent"},
    linear-gradient(45deg, #e9e9ee 25%, transparent 25%, transparent 75%, #e9e9ee 75%) 0 0/16px 16px,
    linear-gradient(45deg, #e9e9ee 25%, #ffffff 25%, #ffffff 75%, #e9e9ee 75%) 8px 8px/16px 16px;
  ${focusRing}
`;
const CFName = styled.span`
  font-family: ${fontFamily};
  font-size: 13px;
  font-weight: 600;
  color: ${INK};
  flex: 1;
`;
const ResetBtn = styled.button`
  min-height: 44px;
  padding: 0 10px;
  border: none;
  background: transparent;
  color: ${PURPLE};
  font-family: ${fontFamily};
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  border-radius: 8px;
  ${focusRing}
  &:hover { background: #faf7fe; }
`;
const NativeColor = styled.input`
  width: 0; height: 0; opacity: 0; position: absolute;
`;

// A compact ColorPopup mirror: swatch opens native picker, optional depth tabs
// (Solid/Gradient) + Preset/Custom, optional opacity/width slider, reset.
export const ColorField = ({
  label = "Background Fill",
  color = "#9B5AF8",
  onChange,
  onReset,
  slider, // { title, value, min, max, unit }
  onSlider,
  depthTabs, // e.g. ["Solid","Gradient"]
  depth,
  onDepth,
}) => {
  const inputRef = React.useRef(null);
  return (
    <div>
      <CFRow>
        <Swatch
          type="button"
          $color={color}
          data-testid="color-field-swatch"
          aria-label={`${label}: choose color`}
          onClick={() => inputRef.current && inputRef.current.click()}
        />
        <CFName>{label}</CFName>
        {onReset && <ResetBtn type="button" onClick={onReset}>Reset</ResetBtn>}
        <NativeColor
          ref={inputRef}
          type="color"
          data-testid="color-field-input"
          value={/^#([0-9a-f]{6})$/i.test(color) ? color : "#9b5af8"}
          aria-label={`${label} color value`}
          onChange={(e) => onChange && onChange(e.target.value)}
        />
      </CFRow>
      {depthTabs && (
        <div style={{ marginTop: 8 }}>
          <SegChoice
            value={depth}
            options={depthTabs.map((t) => ({ label: t, value: t }))}
            onChange={onDepth}
          />
        </div>
      )}
      {slider && (
        <div style={{ marginTop: 10 }}>
          <LabeledSlider
            label={slider.title}
            value={slider.value}
            min={slider.min ?? 0}
            max={slider.max ?? 100}
            unit={slider.unit || ""}
            onChange={onSlider}
          />
        </div>
      )}
    </div>
  );
};

// ── QuadStepper (Padding / Margin T/B/L/R — GORDON DESIGN STANDARD 2026-09-15,
// gordon-0915-law-shared-spacing-control-one-row-steppers) ────────────────────
// This is the presentational-mirror twin of Shared/SpacingControl (see that
// component for the real, app-coupled version used by every block Settings
// pane that goes through MarginBox/PaddingBox). QuadStepper deliberately does
// NOT import Shared/* — this file's own header comment documents why: these
// controls must keep rendering standalone in the isolated harness, without
// the app-coupled dependencies (Redux/craftjs-adjacent) that live under
// Shared/. So the same "one row, collapsible (i) header, no apply-all"
// design is reproduced here self-contained rather than shared by import.
const QuadWrap = styled.div`
  font-family: ${fontFamily};
`;
const QuadHeader = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  width: 100%;
  border: none;
  background: transparent;
  padding: 0;
  cursor: pointer;
  ${focusRing}
`;
const QuadChevron = styled.span`
  margin-left: auto;
  display: inline-flex;
  transform: ${(p) => (p.$expanded ? "rotate(180deg)" : "rotate(0deg)")};
  transition: transform 0.2s ease;
  @media (prefers-reduced-motion: reduce) { transition: none; }
`;
const InfoDot = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 14px; height: 14px;
  border-radius: 50%;
  border: 1.5px solid ${MUTED};
  color: ${MUTED};
  font-size: 10px;
  font-weight: 700;
  font-style: normal;
  cursor: help;
`;
const QuadGrid = styled.div`
  display: flex;
  flex-wrap: nowrap;
  gap: 6px;
  margin-top: 10px;
`;
const SideBox = styled.div`
  display: flex;
  flex: 1 1 0;
  min-width: 0;
  flex-direction: column;
  align-items: stretch;
  gap: 4px;
  padding: 4px 6px;
  border: 1px solid ${BORDER};
  border-radius: 8px;
`;
const SideName = styled.span`
  font-size: 11px;
  font-weight: 600;
  color: ${MUTED};
`;
const StepRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 4px;
`;
const StepBtn = styled.button`
  width: 28px; height: 28px; flex: none;
  border: 1px solid ${BORDER};
  border-radius: 6px;
  background: #fff;
  color: ${PURPLE};
  font-size: 14px;
  line-height: 1;
  cursor: pointer;
  ${focusRing}
  &:hover { background: #faf7fe; }
`;
const StepVal = styled.span`
  min-width: 20px;
  text-align: center;
  font-size: 13px;
  font-weight: 600;
  color: ${INK};
`;

const SIDES = [
  { key: "top", label: "Top" },
  { key: "bottom", label: "Bottom" },
  { key: "left", label: "Left" },
  { key: "right", label: "Right" },
];

export const QuadStepper = ({
  label = "Margin",
  tooltip,
  value = { top: 0, right: 0, bottom: 0, left: 0 },
  min = 0,
  max = 100,
  onChange, // (side, newValue)
  fullWidth = false,
  defaultExpanded = false,
}) => {
  const [expanded, setExpanded] = React.useState(defaultExpanded);
  const set = (side, delta) => {
    const next = Math.min(max, Math.max(min, (Number(value[side]) || 0) + delta));
    onChange && onChange(side, next);
  };
  const visibleSides = SIDES.filter((s) => !(fullWidth && (s.key === "left" || s.key === "right")));
  return (
    <QuadWrap>
      <QuadHeader type="button" aria-expanded={expanded} onClick={() => setExpanded((e) => !e)}>
        <FieldLabel as="span">{label}</FieldLabel>
        {tooltip && <InfoDot title={tooltip} aria-label={`${label} info`}>i</InfoDot>}
        <QuadChevron $expanded={expanded} aria-hidden="true">▾</QuadChevron>
      </QuadHeader>
      {expanded && (
        <QuadGrid>
          {visibleSides.map((s) => (
            <SideBox key={s.key}>
              <SideName>{s.label}</SideName>
              <StepRow>
                <StepBtn type="button" aria-label={`Decrease ${label} ${s.label}`} onClick={() => set(s.key, -1)}>
                  &minus;
                </StepBtn>
                <StepVal aria-label={`${label} ${s.label}`}>{value[s.key] ?? 0}</StepVal>
                <StepBtn type="button" aria-label={`Increase ${label} ${s.label}`} onClick={() => set(s.key, +1)}>
                  +
                </StepBtn>
              </StepRow>
            </SideBox>
          ))}
        </QuadGrid>
      )}
    </QuadWrap>
  );
};

// ── TabsRow (Image / Text / Layout; Upload / Embed URL / Browse / Recent) ─────
const TabsBar = styled.div`
  display: flex;
  gap: 2px;
  border-bottom: 1px solid ${BORDER};
  font-family: ${fontFamily};
`;
const Tab = styled.button`
  min-height: 44px;
  padding: 0 12px;
  border: none;
  background: transparent;
  color: ${(p) => (p.$active ? PURPLE : MUTED)};
  font-family: ${fontFamily};
  font-size: 13px;
  font-weight: ${(p) => (p.$active ? 700 : 500)};
  cursor: pointer;
  position: relative;
  ${focusRing}
  &::after {
    content: "";
    position: absolute;
    left: 8px; right: 8px; bottom: -1px;
    height: 2px;
    border-radius: 2px;
    background: ${(p) => (p.$active ? PURPLE : "transparent")};
  }
`;

export const TabsRow = ({ tabs, active, onChange, ariaLabel = "Editor sections" }) => (
  <TabsBar role="tablist" aria-label={ariaLabel}>
    {tabs.map((t) => (
      <Tab
        key={t}
        type="button"
        role="tab"
        aria-selected={active === t}
        $active={active === t}
        onClick={() => onChange && onChange(t)}
      >
        {t}
      </Tab>
    ))}
  </TabsBar>
);

// ── FontSelect (9 curated fonts, matches the app's font menu) ─────────────────
export const APP_FONTS = [
  "DM Sans", "Arial", "Georgia", "Impact", "Poppins",
  "Quando", "Tahoma", "Times New Roman", "Verdana",
];
// FontSelect is now the shared <EditorField> select variant (single field source).
export const FontSelect = ({ label = "Font", value, onChange, fonts = APP_FONTS }) => (
  <EditorField as="select" label={label} value={value} onChange={onChange}>
    {fonts.map((f) => (
      <option key={f} value={f} style={{ fontFamily: f }}>{f}</option>
    ))}
  </EditorField>
);

// ── TypeStyleCard (Heading Text / Paragraph Text: color + font + size) ────────
const Card = styled.div`
  border: 1px solid ${BORDER};
  border-radius: 10px;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  font-family: ${fontFamily};
`;
const CardTitle = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: ${INK};
`;

export const TypeStyleCard = ({ title, style = {}, onChange }) => {
  const patch = (k) => (v) => onChange && onChange({ ...style, [k]: v });
  return (
    <Card>
      <CardTitle>{title}</CardTitle>
      <ColorField
        label="Text color"
        color={style.color || "#123051"}
        onChange={patch("color")}
        onReset={() => patch("color")("#123051")}
      />
      <FontSelect value={style.font || "DM Sans"} onChange={patch("font")} />
      <LabeledSlider
        label="Size"
        value={style.size ?? 16}
        min={0}
        max={96}
        unit="px"
        onChange={patch("size")}
      />
    </Card>
  );
};

// ── PosGrid (shared 3x3 position picker: text-overlay + image-focus) ──────────
const Grid3 = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 4px;
  width: 140px;
`;
const Cell = styled.button`
  aspect-ratio: 1;
  border: 1px solid ${(p) => (p.$active ? PURPLE : BORDER)};
  border-radius: 6px;
  background: ${(p) => (p.$active ? "#ede4fe" : "#fff")};
  cursor: pointer;
  ${focusRing}
  &:hover { border-color: ${PURPLE}; }
`;

const POS = [
  "top-left", "top-center", "top-right",
  "middle-left", "middle-center", "middle-right",
  "bottom-left", "bottom-center", "bottom-right",
];

export const PosGrid = ({ label = "Position", value = "middle-center", onChange }) => {
  const groupId = useId();
  return (
    <div>
      <FieldLabel id={groupId} as="div">{label}</FieldLabel>
      <Grid3 role="radiogroup" aria-labelledby={groupId}>
        {POS.map((p) => (
          <Cell
            key={p}
            type="button"
            role="radio"
            aria-checked={value === p}
            aria-label={p.replace("-", " ")}
            $active={value === p}
            onClick={() => onChange && onChange(p)}
          />
        ))}
      </Grid3>
    </div>
  );
};

// ── SelectField (native <select>, styled to the field chrome) ─────────────────
// WL-011f: ported from the WL-011 framework branch so the tranche-2 + tranche-3
// list editors (Social platform picker, ImageGrid columns, LinkWheel, etc.) can
// compose a single design-conformant dropdown. FIELD_BG/BORDER/INK/PURPLE all
// come from the shared field chrome (EditorField / tokens.field).
const SelectEl = styled.select`
  width: 100%;
  min-height: 44px;
  padding: 0 12px;
  border: 1px solid ${(p) => (p.$invalid ? "#d92d20" : BORDER)};
  border-radius: 8px;
  background: ${FIELD_BG};
  font-family: ${fontFamily};
  font-size: 14px;
  color: ${INK};
  box-sizing: border-box;
  cursor: pointer;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
  &:hover { border-color: #c8cdd6; }
  &:focus-visible { outline: none; border-color: ${PURPLE}; box-shadow: 0 0 0 3px #ede4fe; }
  @media (prefers-reduced-motion: reduce) { transition: none; }
`;

export const SelectField = ({ label, value, options = [], onChange, placeholder, id }) => {
  const autoId = useId();
  const fieldId = id || autoId;
  return (
    <div>
      {label && <FieldLabel htmlFor={fieldId}>{label}</FieldLabel>}
      <SelectEl
        id={fieldId}
        value={value ?? ""}
        onChange={(e) => onChange && onChange(e.target.value)}
      >
        {placeholder && <option value="">{placeholder}</option>}
        {options.map((o) => {
          const val = typeof o === "string" ? o : o.value;
          const lab = typeof o === "string" ? o : o.label;
          return <option key={val} value={val}>{lab}</option>;
        })}
      </SelectEl>
    </div>
  );
};

// ── RepeatableList (Buttons / Social links / Audio tracks / ImageGrid rows) ───
// A DRY add/remove list. `renderRow(item, i)` composes the per-row fields; this
// owns the "Add" / "Remove" chrome (>=44px targets, focus rings, aria labels).
// WL-011f: ported from the WL-011 framework branch.
const ListWrap = styled.div`
  font-family: ${fontFamily};
  display: flex;
  flex-direction: column;
  gap: 12px;
`;
const ListLabel = styled.div`
  font-family: ${fontFamily};
  font-size: 12px;
  font-weight: 600;
  color: ${INK};
`;
const RowCard = styled.div`
  position: relative;
  border: 1px solid ${BORDER};
  border-radius: 10px;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  background: #fbfcfe;
`;
const RemoveBtn = styled.button`
  align-self: flex-end;
  min-height: 44px;
  padding: 0 12px;
  border: 1px solid ${BORDER};
  border-radius: 8px;
  background: #fff;
  color: #d92d20;
  font-family: ${fontFamily};
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  ${focusRing}
  &:hover { background: #fef3f2; border-color: #fda29b; }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
`;
const AddBtn = styled.button`
  min-height: 44px;
  padding: 0 14px;
  border: 1px dashed ${PURPLE};
  border-radius: 8px;
  background: #f7f2ff;
  color: #7b3fd6;
  font-family: ${fontFamily};
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  ${focusRing}
  &:hover { background: #efe4fe; }
`;
const EmptyHint = styled.div`
  font-family: ${fontFamily};
  font-size: 12px;
  color: ${MUTED};
  padding: 12px;
  border: 1px dashed ${BORDER};
  border-radius: 10px;
  text-align: center;
`;

export const RepeatableList = ({
  label,
  items = [],
  renderRow,
  onAdd,
  onRemove,
  addLabel = "Add",
  minItems = 0,
  maxItems = 20,
  emptyHint,
  rowLabel = "Item",
}) => (
  <ListWrap>
    {label && <ListLabel>{label}</ListLabel>}
    {items.length === 0 && emptyHint && <EmptyHint>{emptyHint}</EmptyHint>}
    {items.map((item, i) => (
      <RowCard key={item.id ?? i}>
        {renderRow(item, i)}
        <RemoveBtn
          type="button"
          aria-label={`Remove ${rowLabel} ${i + 1}`}
          disabled={items.length <= minItems}
          onClick={() => onRemove && onRemove(i)}
        >
          Remove
        </RemoveBtn>
      </RowCard>
    ))}
    {items.length < maxItems && (
      <AddBtn type="button" onClick={() => onAdd && onAdd()}>
        + {addLabel}
      </AddBtn>
    )}
  </ListWrap>
);

const controls = {
  TextInputField,
  LabeledSlider,
  SegChoice,
  Toggle,
  ColorField,
  QuadStepper,
  TabsRow,
  FontSelect,
  TypeStyleCard,
  PosGrid,
  SelectField,
  RepeatableList,
  APP_FONTS,
};

export default controls;
